import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { authApi } from '../services/api';
import type { UserInfo } from '../types';

export const TOKEN_KEY = 'store_token';
export const USER_KEY = 'store_user';
export const STORE_AUTH_EVENT_KEY = 'petflow:store-auth:event';
const STORE_AUTH_CHANNEL = 'petflow-store-auth';
const STORE_AUTH_SOURCE_ID = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const shouldReloadForStoreAuthEvent = (
  rawValue: string | null,
  currentSourceId: string,
) => {
  if (!rawValue) return false;
  try {
    const event = JSON.parse(rawValue);
    return event?.type === 'store_switched' && event?.source_id !== currentSourceId;
  } catch {
    return false;
  }
};

interface AuthContextValue {
  isAuthenticated: boolean;
  user: UserInfo | null;
  token: string | null;
  loading: boolean;
  authReady: boolean;
  rbacReady: boolean;
  login: (tokenVal: string, userInfo: UserInfo) => void;
  switchStoreSession: (tokenVal: string, userInfo: UserInfo) => void;
  logout: () => void;
  refreshUserInfo: (tokenOverride?: string) => Promise<UserInfo>;
  hasRole: (roleCode: string) => boolean;
  hasPage: (menuCode: string) => boolean;
  hasButton: (menuCode: string, buttonCode: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const readStoredToken = () => localStorage.getItem(TOKEN_KEY);

const clearStoredAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const buildUserInfoFromResponse = (data: any): UserInfo => ({
  emp_id: data.emp_id,
  emp_name: data.emp_name,
  mobile: data.mobile,
  store_id: data.store_id,
  store_name: data.store_name,
  role_code: data.role_code,
  menus: data.menus || [],
  routes: data.routes || [],
  page_permissions: data.page_permissions || [],
  button_permissions: data.button_permissions || [],
});

const collectPagePermissionsFromMenus = (menus: any[] = [], result: string[] = []) => {
  menus.forEach((menu) => {
    if (menu?.menu_code) {
      result.push(menu.menu_code);
    }
    collectPagePermissionsFromMenus(menu?.children || [], result);
  });
  return result;
};

const normalizeUserInfo = (userInfo: UserInfo): UserInfo => {
  const pagePermissions = userInfo.page_permissions?.length
    ? userInfo.page_permissions
    : collectPagePermissionsFromMenus(userInfo.menus || []);
  return {
    ...userInfo,
    menus: userInfo.menus || [],
    routes: userInfo.routes || [],
    page_permissions: pagePermissions,
    button_permissions: userInfo.button_permissions || [],
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => readStoredToken());
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(() => !!readStoredToken());
  const [authReady, setAuthReady] = useState<boolean>(() => !readStoredToken());
  const [rbacReady, setRbacReady] = useState<boolean>(false);
  const refreshPromiseRef = useRef<Promise<UserInfo> | null>(null);

  const commitUserInfo = useCallback((tokenVal: string, userInfo: UserInfo) => {
    localStorage.setItem(TOKEN_KEY, tokenVal);
    localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
    setToken(tokenVal);
    setUser(userInfo);
    setAuthReady(true);
    setRbacReady(true);
    setLoading(false);
  }, []);

  const clearAuthState = useCallback(() => {
    clearStoredAuth();
    setToken(null);
    setUser(null);
    setAuthReady(true);
    setRbacReady(false);
    setLoading(false);
  }, []);

  const refreshUserInfo = useCallback(async (tokenOverride?: string) => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const activeToken = tokenOverride || readStoredToken();
    if (!activeToken) {
      clearAuthState();
      return Promise.reject(new Error('缺少登录凭证'));
    }

    setToken(activeToken);
    setLoading(true);
    setAuthReady(false);
    setRbacReady(false);

    refreshPromiseRef.current = (async () => {
      try {
        localStorage.setItem(TOKEN_KEY, activeToken);
        const res: any = await authApi.getUserInfo({ silent: true, silentPermission: true });
        if (res?.permissionDenied || res?.code === 403) {
          throw new Error(res?.msg || '权限信息获取失败');
        }
        if (res?.code && res.code !== 200) {
          throw new Error(res?.msg || '权限信息获取失败');
        }
        const nextUser = buildUserInfoFromResponse(res.data || {});
        commitUserInfo(activeToken, nextUser);
        return nextUser;
      } catch (error) {
        clearAuthState();
        throw error;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [clearAuthState, commitUserInfo]);

  useEffect(() => {
    const activeToken = readStoredToken();
    if (!activeToken) {
      clearAuthState();
      return;
    }

    let cancelled = false;
    refreshUserInfo(activeToken).catch(() => {
      if (!cancelled) {
        clearAuthState();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [clearAuthState, refreshUserInfo]);

  useEffect(() => {
    const handleStoreSwitched = (rawValue: string | null) => {
      if (shouldReloadForStoreAuthEvent(rawValue, STORE_AUTH_SOURCE_ID)) {
        window.location.assign('/');
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORE_AUTH_EVENT_KEY) {
        handleStoreSwitched(event.newValue);
      }
    };

    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(STORE_AUTH_CHANNEL);
      channel.onmessage = (event) => handleStoreSwitched(JSON.stringify(event.data));
    }
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      channel?.close();
    };
  }, []);

  const pagePermissions = useMemo(() => (
    new Set(user?.page_permissions || [])
  ), [user?.page_permissions]);

  const buttonPermissions = useMemo(() => (
    new Set(user?.button_permissions || [])
  ), [user?.button_permissions]);

  const isAuthenticated = useMemo(() => !!token && !!user, [token, user]);

  const login = useCallback((tokenVal: string, userInfo: UserInfo) => {
    commitUserInfo(tokenVal, normalizeUserInfo(userInfo));
  }, [commitUserInfo]);

  const switchStoreSession = useCallback((tokenVal: string, userInfo: UserInfo) => {
    const normalizedUser = normalizeUserInfo(userInfo);
    commitUserInfo(tokenVal, normalizedUser);

    const event = {
      type: 'store_switched',
      store_id: normalizedUser.store_id,
      event_id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      source_id: STORE_AUTH_SOURCE_ID,
    };
    localStorage.setItem(STORE_AUTH_EVENT_KEY, JSON.stringify(event));
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(STORE_AUTH_CHANNEL);
      channel.postMessage(event);
      channel.close();
    }
  }, [commitUserInfo]);

  const logout = useCallback(() => {
    clearAuthState();
    window.location.href = '/login';
  }, [clearAuthState]);

  const hasRole = useCallback((roleCode: string) => {
    return user?.role_code === roleCode;
  }, [user?.role_code]);

  const hasPage = useCallback((menuCode: string) => {
    if (!menuCode) return false;
    return pagePermissions.has(menuCode);
  }, [pagePermissions]);

  const hasButton = useCallback((menuCode: string, buttonCode: string) => {
    if (!menuCode || !buttonCode) return false;
    if (!hasPage(menuCode)) return false;
    return buttonPermissions.has(`${menuCode}:${buttonCode}`);
  }, [buttonPermissions, hasPage]);

  const value = useMemo<AuthContextValue>(() => ({
    isAuthenticated,
    user,
    token,
    loading,
    authReady,
    rbacReady,
    login,
    switchStoreSession,
    logout,
    refreshUserInfo,
    hasRole,
    hasPage,
    hasButton,
  }), [
    isAuthenticated,
    user,
    token,
    loading,
    authReady,
    rbacReady,
    login,
    switchStoreSession,
    logout,
    refreshUserInfo,
    hasRole,
    hasPage,
    hasButton,
  ]);

  return React.createElement(AuthContext.Provider, { value }, children);
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
